import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2, UserCircle2, Users2 } from "lucide-react";

import {
  OnboardingShell,
} from "@/components/onboarding/OnboardingShell";
import { OnboardingAfterTrail } from "@/components/onboarding/OnboardingAfterTrail";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { BASE_ONBOARDING_TRAIL } from "@/components/onboarding/OnboardingTrail";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";

export type WhoSetupChoice = "self" | "accountant";

export interface WhoSetupStepProps {
  selected: WhoSetupChoice | null;
  onSelect: (value: WhoSetupChoice) => void;
  /** Saves onboardingMode and advances the wizard. Wired to handleWhoSetupSubmit in useOnboarding. */
  onSubmit: () => void | Promise<void>;
  isSaving: boolean;
  /** Tenant legal name (or trade name) for the H1 ("Preparemos LUIS CAL SAS para facturar."). */
  companyDisplayName?: string;
  /** Optional accountant email reveal. Pass both to render. */
  accountantEmail?: string;
  onAccountantEmailChange?: (email: string) => void;
}

/**
 * Paso 04 — Quién configura. Full OnboardingShell with brand column and
 * internal actions row. The accent CTA copy adapts to the current choice
 * ("Empezar configuración" / "Enviar invitación").
 */
export function WhoSetupStep({
  selected,
  onSelect,
  onSubmit,
  isSaving,
  companyDisplayName,
  accountantEmail,
  onAccountantEmailChange,
}: WhoSetupStepProps): JSX.Element {
  const { t } = useTranslation();
  const accountantInputVisible =
    accountantEmail !== undefined && onAccountantEmailChange !== undefined;
  const accountantEmailRequired =
    selected === "accountant" &&
    accountantInputVisible &&
    !(accountantEmail ?? "").trim();
  const ctaDisabled = !selected || isSaving || accountantEmailRequired;

  const ctaCopy =
    selected === "accountant"
      ? t("onboarding.step4.ctaAccountantContinue", "Continuar")
      : t("onboarding.step4.ctaSelf", "Empezar configuración");

  const headline =
    companyDisplayName?.trim()
      ? t("onboarding.step4.h1", "Preparemos {{name}} para facturar.", {
          name: companyDisplayName.trim(),
        })
      : t(
          "onboarding.step4.h1Fallback",
          "Preparemos tu empresa para facturar.",
        );

  return (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={4}
            total={4}
            label={t("onboarding.step4.tag", "Quién sigue")}
          />
          <h1 className="fx-onb-h1">{headline}</h1>
          <p className="fx-onb-lead">
            {t(
              "onboarding.step4.lead",
              "Falta la configuración técnica de DGI (CFE, series, certificado). Elegí quién la completa — vos o tu contador.",
            )}
          </p>

          <div className="fx-onb-choice-grid">
            <OnboardingChoiceCard
              icon={<UserCircle2 aria-hidden />}
              name={
                <>
                  {t("onboarding.step4.selfNameLead", "Lo hago")}{" "}
                  <span className="ital">
                    {t("onboarding.step4.selfNameAccent", "yo.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.step4.selfDesc",
                "Te guiamos paso a paso.",
              )}
              meta={t("onboarding.step4.selfMeta", "→ Configurás vos mismo")}
              selected={selected === "self"}
              onClick={() => onSelect("self")}
            />

            <OnboardingChoiceCard
              icon={<Users2 aria-hidden />}
              name={
                <>
                  {t("onboarding.step4.accountantNameLead", "Lo hace mi")}{" "}
                  <span className="ital">
                    {t("onboarding.step4.accountantNameAccent", "contador.")}
                  </span>
                </>
              }
              desc={t(
                "onboarding.step4.accountantDesc",
                "Le mandamos un link único. Vos seguís usando Fiscalo desde ya.",
              )}
              meta={t(
                "onboarding.step4.accountantMeta",
                "→ Invitación por correo",
              )}
              selected={selected === "accountant"}
              onClick={() => onSelect("accountant")}
            />
          </div>

          {accountantInputVisible && selected === "accountant" ? (
            <div className="fx-onb-form" style={{ marginTop: 4 }}>
              <div className="fx-onb-field">
                <label htmlFor="who-setup-accountant-email">
                  {t(
                    "onboarding.step4.accountantEmailLabel",
                    "Correo del contador",
                  )}
                </label>
                <input
                  id="who-setup-accountant-email"
                  type="email"
                  autoComplete="email"
                  placeholder="contador@email.com"
                  value={accountantEmail}
                  onChange={(e) => onAccountantEmailChange?.(e.target.value)}
                />
                <span className="hint">
                  ✓{" "}
                  {t(
                    "onboarding.step4.accountantEmailHint",
                    "Le llega un correo con instrucciones. Vos seguís sin esperarlo.",
                  )}
                </span>
              </div>
            </div>
          ) : null}

          <div className="fx-onb-actions">
            <div className="side" />
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={ctaDisabled}
                onClick={() => {
                  if (ctaDisabled) return;
                  void onSubmit();
                }}
              >
                {isSaving ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {ctaCopy}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={
        <OnboardingBrandColumn
          eyebrow={t(
            "onboarding.step4.brandEyebrow",
            "Tu equipo, tu ritmo",
          )}
          quote={
            <>
              {t(
                "onboarding.step4.brandQuoteLead",
                "Si tenés contador,",
              )}{" "}
              <span className="accent">
                {t(
                  "onboarding.step4.brandQuoteAccent",
                  "invitalo en un click.",
                )}
              </span>{" "}
              {t(
                "onboarding.step4.brandQuoteTail",
                "Sigue todo desde su lado.",
              )}
            </>
          }
          currentStep={4}
          trailSteps={BASE_ONBOARDING_TRAIL}
          afterTrail={<OnboardingAfterTrail />}
          valueBlock={{
            head: t(
              "onboarding.step4.valueHead",
              "Cuando invitás a tu contador",
            ),
            items: [
              t(
                "onboarding.step4.valueItem1",
                "Le mandamos un link único y seguro",
              ),
              t(
                "onboarding.step4.valueItem2",
                "Configura todo desde su lado",
              ),
              t(
                "onboarding.step4.valueItem3",
                "Vos seguís usando Fiscalo desde ya",
              ),
            ],
          }}
        />
      }
    />
  );
}
